import React, { useState, useRef } from 'react';
import { postGlobalLeadCapture } from "@/pages/api";
import { Calendar } from 'lucide-react'
import { useRouter } from "next/router";
import { createCRMLeadEnquiryBody } from "@/utils/utils";
import { CRM_LEADS_FIELD_MAP } from '@/commons/constants';



const EnquiryBookingForm = ({ data , languageSlug}) => {
    // Default fallback if data is missing, though we expect it passed
    const title = data?.title || "Book Your Puja in 2 Minutes";
    const subtitle = data?.subtitle || "Simple. Fast. Blessed.";
    const locations = data?.locations || []; // Expecting array of strings: ["Bandra", "Andheri", ...]
    const languages = data?.languages || [];

    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        city: '',
        language: '',
        pujaType: '',
        date: '',
        dateSelection: '', // 'auspicious' or 'manual'
        notes: '',
        antiConfusion: false
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const dateInputRef = useRef(null);
    const router = useRouter();
    // Reset form
    const resetForm = () => {
        setFormData({
            name: '',
            phone: '',
            city: '',
            language: '',
            pujaType: '',
            date: '',
            dateSelection: '',
            notes: '',
            antiConfusion: false
        });
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleDateSelectionChange = (e) => {
        const value = e.target.value;
        if (value === 'manual') {
            setFormData({
                ...formData,
                dateSelection: 'manual',
                date: ''
            });
            setTimeout(() => {
                if (dateInputRef.current) {
                    dateInputRef.current.focus();
                    dateInputRef.current.showPicker?.();
                }
            }, 100);
        } else if (value === 'auspicious') {
            setFormData({
                ...formData,
                dateSelection: 'auspicious',
                date: ''
            });
        }
    };
  const capitalize = (str = "") =>
    str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const payload = createCRMLeadEnquiryBody({
                ...formData,
                enquiry: `Language: ${formData?.language} - Puja Type: ${formData?.pujaType} - Pooja Selected Date: ${formData?.date} - Enquiry: ${formData?.notes}`,
            });
            const res = await postGlobalLeadCapture({
                ...payload,
                [CRM_LEADS_FIELD_MAP?.pujaType]: capitalize(payload?.['pooja-enquired']),
                source: "Enquiry Form",
                webPageLink: process.env.NEXT_PUBLIC_ENQUIRY_URL + router.asPath,
                [CRM_LEADS_FIELD_MAP.city]:  languageSlug? `${languageSlug?.toUpperCase()}: ${payload?.city}`: payload?.city,
            });
            resetForm();
            router.push("/thank-you");
        } catch (error) {
            console.error("Error submitting form:", error);
            alert("Something went wrong. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const isFormValid = () => {
        return formData.name &&
            formData.phone &&
            formData.city &&
            formData.language &&
            formData.pujaType &&
            (formData.dateSelection === 'auspicious' || (formData.dateSelection === 'manual' && formData.date)) &&
            formData.antiConfusion;
    };

    return (
        <>
            <section id="booking-form" className="py-16 px-6 bg-[#FEEDD5]">
                <div className="max-w-xl mx-auto">
                    <div className="text-center mb-8">
                        <h2 className="text-3xl font-bold text-[#8A1C23] mb-4">{title}</h2>
                        <p className="text-lg text-gray-700">
                            {subtitle}
                        </p>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow space-y-4">
                        <div className="space-y-4">
                            <div>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className="w-full border border-gray-300 p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8A1C23] focus:border-transparent"
                                    placeholder="Your Name *"
                                    required
                                    disabled={isSubmitting}
                                />
                            </div>

                            <div>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    className="w-full border border-gray-300 p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8A1C23] focus:border-transparent"
                                    placeholder="Phone (WhatsApp) *"
                                    required
                                    disabled={isSubmitting}
                                />
                            </div>

                            <div>
                                {locations.length > 0 ? (
                                    <>
                                        <label htmlFor="city-select" className="sr-only">Select Your Area</label>
                                        <select
                                            id="city-select"
                                            name="city"
                                            value={formData.city}
                                            onChange={handleInputChange}
                                            className="w-full border border-gray-300 p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8A1C23] focus:border-transparent"
                                            required
                                            disabled={isSubmitting}
                                        >
                                            <option value="">Select Your Area *</option>
                                            {locations.map((loc, idx) => (
                                                <option key={idx} value={loc}>{loc}</option>
                                            ))}
                                        </select>
                                    </>
                                ) : (
                                    <input
                                        type="text"
                                        name="city"
                                        value={formData.city}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8A1C23] focus:border-transparent"
                                        placeholder="Your City *"
                                        required
                                        disabled={isSubmitting}
                                    />
                                )}
                            </div>

                             <div>
                                <label htmlFor="language-select" className="sr-only">Select Language Preference</label>
                                <select
                                    id="language-select"
                                    name="language"
                                    value={formData.language}
                                    onChange={handleInputChange}
                                    className="w-full border border-gray-300 p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8A1C23] focus:border-transparent"
                                    required
                                    disabled={isSubmitting}
                                >
                                    <option value="">Select Language Preference *</option>
                                    {languages.length > 0 ? (
                                        languages.map((lang, idx) => (
                                            <option key={idx} value={lang}>{lang}</option>
                                        ))
                                    ) : (
                                        <>
                                            <option value="Hindi">Hindi</option>
                                            <option value="English">English</option>
                                            {/* Fallback if data not loaded */}
                                        </>
                                    )}
                                </select>
                            </div>

                             <div>
                                <label htmlFor="puja-select" className="sr-only">Select Puja Type</label>
                                <select
                                    id="puja-select"
                                    name="pujaType"
                                    value={formData.pujaType}
                                    onChange={handleInputChange}
                                    className="w-full border border-gray-300 p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8A1C23] focus:border-transparent"
                                    required
                                    disabled={isSubmitting}
                                >
                                    <option value="">Select Puja Type *</option>
                                    <option value="griha-pravesh">Griha Pravesh (Housewarming)</option>
                                    <option value="satyanarayan">Satyanarayan Puja</option>
                                    <option value="ganesh">Ganesh Puja</option>
                                    <option value="navgraha">Navagraha Puja</option>
                                    <option value="mahamrityunjaya">Mahamrityunjay Jaap</option>
                                    <option value="office">Office/Business Puja</option>
                                    <option value="lakshmi">Lakshmi Puja</option>
                                    <option value="vastu">Vastu Shanti</option>
                                    <option value="shashtipoorthi">Shashtipoorthi</option>
                                    <option value="other">Other (mention in notes)</option>
                                </select>
                            </div>

                            {/* Date Selection Section */}
                            <div className="space-y-3">
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                    Do you have a specific date in mind for your puja? *
                                </label>

                                <div className="space-y-3">
                                    <div className="flex items-center">
                                        <input
                                            type="radio"
                                            id="manual-date"
                                            name="dateSelectionRadio"
                                            value="manual"
                                            checked={formData.dateSelection === 'manual'}
                                            onChange={handleDateSelectionChange}
                                            className="h-4 w-4 text-[#8A1C23] focus:ring-[#8A1C23] border-gray-300"
                                            disabled={isSubmitting}
                                        />
                                        <label htmlFor="manual-date" className="ml-3 text-gray-700 font-medium cursor-pointer">
                                            📅 Yes, I want to select a specific date
                                        </label>
                                    </div>

                                    <div className="flex items-center">
                                        <input
                                            type="radio"
                                            id="auspicious-date"
                                            name="dateSelectionRadio"
                                            value="auspicious"
                                            checked={formData.dateSelection === 'auspicious'}
                                            onChange={handleDateSelectionChange}
                                            className="h-4 w-4 text-[#8A1C23] focus:ring-[#8A1C23] border-gray-300"
                                            disabled={isSubmitting}
                                        />
                                        <label htmlFor="auspicious-date" className="ml-3 text-gray-700 font-medium cursor-pointer">
                                            🌟 No, I need help finding the most auspicious date
                                        </label>
                                    </div>
                                </div>

                                {formData.dateSelection === 'manual' && (
                                    <div className="mt-3">
                                        <label htmlFor="date-input" className="sr-only">Choose Date</label>
                                        <input
                                            id="date-input"
                                            ref={dateInputRef}
                                            type="date"
                                            name="date"
                                            value={formData.date}
                                            onChange={handleInputChange}
                                            min={new Date().toISOString().split('T')[0]}
                                            className="w-full border border-gray-300 p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8A1C23] focus:border-transparent"
                                            required
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                )}

                                {formData.dateSelection === 'auspicious' && (
                                    <div className="p-4 bg-green-50 border border-green-200 rounded-md mt-3">
                                        <div className="flex items-center gap-2 text-green-700">
                                            <span className="text-lg">✅</span>
                                            <span className="font-medium">Great choice! We&#39;ll suggest the most auspicious dates for your puja.</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div>
                                <textarea
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleInputChange}
                                    rows={3}
                                    className="w-full border border-gray-300 p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8A1C23] focus:border-transparent"
                                    placeholder="Additional Information (Optional)"
                                    disabled={isSubmitting}
                                />
                            </div>

                            {/* Anti-Confusion Checkbox */}
                            <div className="flex items-start bg-yellow-50 p-3 rounded-md border border-yellow-200">
                                <div className="flex items-center h-5">
                                    <input
                                        id="antiConfusion"
                                        name="antiConfusion"
                                        type="checkbox"
                                        checked={formData.antiConfusion}
                                        onChange={handleInputChange}
                                        className="focus:ring-[#8A1C23] h-4 w-4 text-[#8A1C23] border-gray-300 rounded"
                                        required
                                        disabled={isSubmitting}
                                    />
                                </div>
                                <div className="ml-3 text-sm">
                                    <label htmlFor="antiConfusion" className="font-medium text-gray-700">
                                        I understand this is an enquiry. A coordinator will call to confirm details.
                                    </label>
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={handleSubmit}
                                className="bg-[#8A1C23] text-white py-3 px-6 rounded-md w-full font-medium hover:bg-[#6B1419] transition-colors text-lg shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                disabled={isSubmitting || !isFormValid()}
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Submitting...
                                    </>
                                ) : (
                                    'Request a Callback'
                                )}
                            </button>

                            <p className="text-sm text-gray-600 text-center">
                                We&#39;ll call within 2 hours • Transparent and Hassle free packages to choose from
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Mobile Sticky Reminder Bar (Keeping existing) */}
            <div className="sticky bottom-0 bg-white shadow-md p-4 z-40 md:hidden border-t">
                <div className="flex justify-between items-center">
                    <div>
                        <div className="text-sm font-medium text-[#8A1C23]">Ready to book your puja?</div>
                        <div className="text-xs text-gray-500">2 minutes • Convenient Packages</div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => window.open('tel:+919869929292', '_self')}
                            className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white py-2 px-4 rounded-md text-sm font-medium flex items-center gap-1 min-w-[80px] justify-center transition-colors duration-200 shadow-md hover:shadow-lg"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                strokeLinejoin="round">
                                <path
                                    d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                            </svg>
                            Call
                        </button>
                        <button
                            onClick={() =>
                                document.getElementById("booking-form")?.scrollIntoView({ behavior: "smooth" })
                            }
                            className="bg-[#8A1C23] text-white py-2 px-4 rounded-md text-sm font-medium min-w-[100px] justify-center flex items-center gap-2"
                        >
                            <Calendar className="w-4 h-4" />
                            Book
                        </button>
                    </div>
                </div>
            </div>

            <style jsx>{`
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1);
                    }
                }
                @keyframes spin {
                    to {
                        transform: rotate(360deg);
                    }
                }
            `}</style>
        </>
    );
};

export default EnquiryBookingForm;

